import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useApiClient } from '../../auth/hooks/use-api-client.hook';
import { DEFAULT_STALE_TIME } from '../core/constants';
import { ResourceNotFoundError } from '../core/errors';
import { ResourceQueryConfig } from '../core/types';
import { useResourceEndpoints } from '../core/use-resource-endpoints.hook';
import { buildFieldsString, handleResourceError } from '../utils/resource-hooks.utils';

export interface ResourceQueryVariables {
	/** The resource ID */
	resourceId?: string;
	/** Optional field selection (API: _fields) */
	_fields?: string[];
	/** The method name to call */
	callMethod?: string;
	/** Additional variables */
	[key: string]: unknown;
}

export const getResourceQueryKey = (
	variables: Partial<ResourceQueryVariables>,
	resourceType: string,
	keyPrefix?: string
) => {
	const { resourceId, ...rest } = variables;
	const prefix = keyPrefix || resourceType.replace(/s$/, '').toLowerCase();
	const queryKey = [prefix];
	if (resourceId) {
		queryKey.push(resourceId);
	}
	if (Object.keys(rest).length > 0) {
		const queryParams = Object.entries(rest).map(([key, value]) => {
			if (Array.isArray(value)) {
				return `${key}=${value.join(',')}`;
			}
			return JSON.stringify(value);
		});
		queryKey.push(queryParams.sort().join('&'));
	}
	return queryKey;
};

/**
 * Hook to invalidate a resource query
 */
export const useInvalidateResourceQuery = (
	variables: ResourceQueryVariables,
	config: Pick<ResourceQueryConfig, 'resourceType' | 'queryKeyPrefix'>
) => {
	const queryClient = useQueryClient();
	const queryKey = useMemo(
		() => getResourceQueryKey(variables, config.resourceType, config.queryKeyPrefix),
		[variables, config.resourceType, config.queryKeyPrefix]
	);

	const invalidateResourceQuery = useCallback(() => {
		if (queryKey) {
			void queryClient.invalidateQueries({ queryKey });
		}
	}, [queryKey, queryClient]);

	return {
		queryKey,
		invalidateResourceQuery,
	} as const;
};

export const useResourceQuery = <TResponse = unknown, TPythonResponse = unknown, TError = unknown, TData = TResponse>(
	variables: ResourceQueryVariables,
	config: ResourceQueryConfig<TResponse, TPythonResponse>,
	options?: UseQueryOptions<TPythonResponse, TError, TData>
) => {
	const apiClient = useApiClient();
	const endpoints = useResourceEndpoints(config.resourceType);
	const fieldsString = useMemo(
		// eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- _fields is string[] | undefined; inferred as any in generic context
		() => buildFieldsString(variables._fields, config.defaultFields),
		[variables._fields, config.defaultFields]
	);
	const queryKey = useMemo(
		() => getResourceQueryKey(variables, config.resourceType, config.queryKeyPrefix),
		[variables, config.resourceType, config.queryKeyPrefix]
	);

	const url = useMemo(() => {
		const { resourceId, _fields, callMethod, ...queryParams } = variables;

		if (resourceId === undefined) return undefined;

		const params: Record<string, unknown> = queryParams ?? {};
		if (fieldsString) {
			params['_fields'] = fieldsString;
		}

		return variables.callMethod
			? endpoints.getCallUrl(resourceId, variables.callMethod, true, params)
			: endpoints.getUrl(resourceId, '', params);
	}, [endpoints, variables, fieldsString]);

	const queryFn = useCallback(async (): Promise<TPythonResponse> => {
		try {
			if (!url) {
				throw new Error('Resource URL is not defined');
			}
			return await apiClient.get<TPythonResponse>(url);
		} catch (error) {
			throw handleResourceError(error);
		}
	}, [url, apiClient]);

	return useQuery<TPythonResponse, TError, TData>(queryKey, queryFn, {
		staleTime: config.staleTime ?? DEFAULT_STALE_TIME,
		enabled: !!variables.resourceId || !!variables.callMethod,
		retry: (failureCount, error) => {
			if (error instanceof ResourceNotFoundError) {
				return false;
			}
			return true;
		},
		...options,
	});
};
